import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_ID, ApplicationConfig, importProvidersFrom, isDevMode, signal } from '@angular/core';
import { provideRouter, withEnabledBlockingInitialNavigation } from '@angular/router';
import { CoreEffects } from '@next/core-lib';
import { coreRoutes, provideAgrCore } from '@next/core-lib/app';
import { AgrBrandingModule } from '@next/core-lib/branding';
import { I18nBrowserEffects, provideAgrBrowser } from '@next/core-lib/browser';
import { AgrNavigationRailModule } from '@next/core-lib/components';
import { provideAgrElasticApi } from '@next/core-lib/elastic';
import { ENVIRONMENT_MAPPER } from '@next/core-lib/environment-browser';

import { I18nEffects, provideAgrI18nShared } from '@next/core-lib/i18n';
import { NavigationEffects, provideAgrNavigation } from '@next/core-lib/navigation';
import { AgrPWAModule } from '@next/core-lib/pwa';
import { provideAgrRouterStore } from '@next/core-lib/router-store';
import { provideAgrSettings } from '@next/core-lib/settings';
import { SiteEffects, provideAgrSite } from '@next/core-lib/site';
import { provideAgrWrapperSiteSidenav } from '@next/core-lib/wrapper-site-sidenav';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { environment } from 'src/environments/environment';

import { routes } from './app.routes';
import { parseNextAdminEnvironment } from '../environment';

declare global {
  var CONFIG_BUILD_ID: string | undefined;
}

const loadedConfig = signal(environment);

export const setupRuntimeConfig = async (): Promise<void> => {
  try {
    const buildId = globalThis.CONFIG_BUILD_ID;
    const configUrl = buildId ? `/assets/config.json?v=${buildId}` : '/assets/config.json';
    const response = await fetch(configUrl);
    if (response.ok) {
      const config = await response.json();
      loadedConfig.set({ ...environment, ...parseNextAdminEnvironment(config) });
    } else {
      console.warn('No runtime configuration found, using default environment settings.');
    }
  } catch (e) {
    console.warn('Failed to load runtime environment variables', e);
  }
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter([...routes, ...coreRoutes], withEnabledBlockingInitialNavigation()),
    provideHttpClient(withInterceptorsFromDi()),
    provideStore(
      {},
      {
        runtimeChecks: {
          strictStateImmutability: false,
          strictActionImmutability: false,
          strictStateSerializability: false,
          strictActionSerializability: false,
        },
      },
    ),
    provideEffects([CoreEffects, I18nEffects, I18nBrowserEffects, NavigationEffects, SiteEffects]),
    provideAgrRouterStore(),
    provideAgrSite(),
    provideAgrCore(),
    provideAgrBrowser(),
    provideAgrElasticApi(),
    provideAgrI18nShared(),
    provideAgrSettings(),
    provideAgrNavigation(),
    provideAgrWrapperSiteSidenav(),
    importProvidersFrom(AgrPWAModule, AgrBrandingModule, AgrNavigationRailModule),
    {
      provide: ENVIRONMENT_MAPPER,
      useFactory: () => loadedConfig(),
    },
    {
      provide: APP_ID,
      useFactory: () => loadedConfig().APPLICATION_NAME,
    },
    provideStoreDevtools({
      maxAge: 25,
      logOnly: !isDevMode(),
      autoPause: true,
      trace: false,
      traceLimit: 75,
      connectInZone: true,
    }),
  ],
};
